// Per-type visual identity: a soft colour + an icon for each notification type,
// shown in the hub and the dropdown. Colours are intentionally soft (100 bg /
// 500-600 icon). Unknown types fall back to a neutral bell.

import {
  Bell,
  Bookmark,
  CalendarDays,
  Heart,
  Home,
  Layers,
  ListChecks,
  MessageSquare,
  RotateCcw,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import type { NotificationType } from "./model.js";

export interface TypeVisual {
  Icon: LucideIcon;
  /** Icon colour class. */
  iconClass: string;
  /** Soft background class for the icon chip. */
  bgClass: string;
}

const VISUALS: Partial<Record<NotificationType, TypeVisual>> = {
  WISHLISTED_ANIMAL_ADOPTED: {
    Icon: Home,
    iconClass: "text-emerald-600",
    bgClass: "bg-emerald-100",
  },
  WISHLISTED_ANIMAL_AVAILABLE: {
    Icon: Sparkles,
    iconClass: "text-purple-600",
    bgClass: "bg-purple-100",
  },
  NEW_MATCHES_PAWMATCH: {
    Icon: Sparkles,
    iconClass: "text-purple-600",
    bgClass: "bg-purple-100",
  },
  WEEKLY_DIGEST: {
    Icon: CalendarDays,
    iconClass: "text-sky-600",
    bgClass: "bg-sky-100",
  },
  FIRST_LIKE_NUDGE: {
    Icon: Heart,
    iconClass: "text-rose-500",
    bgClass: "bg-rose-100",
  },
  COMEBACK_PAWMATCH: {
    Icon: RotateCcw,
    iconClass: "text-amber-600",
    bgClass: "bg-amber-100",
  },
  PAWMATCH_DAILY_SWIPES_READY: {
    Icon: Layers,
    iconClass: "text-indigo-600",
    bgClass: "bg-indigo-100",
  },
  PAWMATCH_MATCH_ADOPTED: {
    Icon: Home,
    iconClass: "text-emerald-600",
    bgClass: "bg-emerald-100",
  },
  PAWMATCH_MATCH_AVAILABLE: {
    Icon: Sparkles,
    iconClass: "text-purple-600",
    bgClass: "bg-purple-100",
  },
  PUBLISHER_MISSING_CHIP_NUMBERS: {
    Icon: ListChecks,
    iconClass: "text-slate-600",
    bgClass: "bg-slate-100",
  },
  ADOPTION_APPLICATION_RECEIVED: {
    Icon: ListChecks,
    iconClass: "text-sky-600",
    bgClass: "bg-sky-100",
  },
  ADOPTION_APPLICATION_STATUS_CHANGED: {
    Icon: Bell,
    iconClass: "text-amber-600",
    bgClass: "bg-amber-100",
  },
  ADOPTION_MESSAGE_RECEIVED: {
    Icon: MessageSquare,
    iconClass: "text-violet-600",
    bgClass: "bg-violet-100",
  },
  ADOPTION_CHECKUP_REMINDER: {
    Icon: CalendarDays,
    iconClass: "text-teal-600",
    bgClass: "bg-teal-100",
  },
  ADOPTION_STEP_CHANGED: {
    Icon: ListChecks,
    iconClass: "text-main-purple",
    bgClass: "bg-purple-100",
  },
  ADOPTION_MANUAL_CHAT_CREATED: {
    Icon: MessageSquare,
    iconClass: "text-sky-600",
    bgClass: "bg-sky-100",
  },
  FIRST_SAVED_SEARCH_NUDGE: {
    Icon: Bookmark,
    iconClass: "text-purple-600",
    bgClass: "bg-purple-100",
  },
};

const FALLBACK: TypeVisual = {
  Icon: Bell,
  iconClass: "text-gray-500",
  bgClass: "bg-gray-100",
};

export function getTypeVisual(type: NotificationType): TypeVisual {
  return VISUALS[type] ?? FALLBACK;
}
